import { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { request as requestNormal, requestSilent } from "@/Api";

const ApiAutocomplete = memo((props) => {
    const {
        apiRoute,
        apiFilters,
        fields,
        fieldSeperator,
        error,
        helperText,
        label,
        fullOptionData,
        inputStyle,
        simple,
        disabled,
        readOnly,
        silentErrors,
        additionalOptions,
        placeholder,
        onInputResponseLimit,
        onLoadResponseData,
        variant,
        renderOption,
        InputProps,
        datatestid,
        extraParams,
        ...passThroughProps
    } = props;
    const [serverEntities, setServerEntities] = useState([]);
    const [loadError, setLoadError] = useState(false);
    const customInputProps = InputProps || {};
    const debounceTimeoutRef = useRef();

    const entities = useMemo(() => {
        const additionalEntities = additionalOptions ? additionalOptions : [];
        return [...serverEntities, ...additionalEntities];
    }, [serverEntities, additionalOptions]);

    const getEntities = useCallback(
        (request, searchString) => {
            if (readOnly || disabled) {
                return;
            }

            request
                .get(apiRoute, {
                    params: {
                        offset: 0,
                        limit:
                            onInputResponseLimit != null
                                ? onInputResponseLimit
                                : 0,
                        simple: simple,
                        ...apiFilters,
                        name:
                            onInputResponseLimit != null ? searchString : null,
                        ...extraParams,
                    },
                })
                .then((response) => {
                    if (onLoadResponseData) {
                        onLoadResponseData(response.data);
                    }
                    setServerEntities(
                        response.data.items
                            ? response.data.items
                            : response.data,
                    );
                })
                .catch(() => {
                    if (!disabled) {
                        setLoadError(true);
                    }
                });
        },
        [
            readOnly,
            disabled,
            apiRoute,
            onInputResponseLimit,
            simple,
            apiFilters,
            onLoadResponseData,
            extraParams,
        ],
    );
    const handleTextFieldChange = (event) => {
        if (onInputResponseLimit) {
            const searchString = event.target.value;
            if (debounceTimeoutRef.current != null) {
                clearTimeout(debounceTimeoutRef.current);
            }
            const debounceTimeout = setTimeout(
                () => {
                    getEntities(
                        silentErrors ? requestSilent : requestNormal,
                        searchString,
                    );
                },
                onInputResponseLimit ? 250 : 0,
            );
            debounceTimeoutRef.current = debounceTimeout;
        }
    };

    useEffect(() => {
        getEntities(silentErrors ? requestSilent : requestNormal, "");
    }, [getEntities, silentErrors]);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current != null) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    if (fullOptionData) {
        passThroughProps.isOptionEqualToValue = (option, value) => {
            return option.id == value.id;
        };
    }

    return (
        <Autocomplete
            {...passThroughProps}
            disabled={disabled || loadError}
            readOnly={readOnly}
            disablePortal
            options={
                fullOptionData ? entities : entities.map((entity) => entity.id)
            }
            getOptionLabel={(idOrObject) => {
                const entity = fullOptionData
                    ? idOrObject
                    : entities.find((entity) => idOrObject == entity.id);
                const labels = [];
                if (entity) {
                    for (const field of fields) {
                        labels.push(entity[field]);
                    }
                }
                return labels.join(fieldSeperator || " ");
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    onChange={handleTextFieldChange}
                    sx={inputStyle}
                    label={label}
                    error={error}
                    helperText={helperText}
                    placeholder={placeholder}
                    variant={variant || "outlined"}
                    InputProps={{ ...params.InputProps, ...customInputProps }}
                    data-testid={datatestid || "api-autocomplete-input"}
                />
            )}
        />
    );
});

ApiAutocomplete.displayName = "ApiAutocomplete";

export default ApiAutocomplete;
